<template>
  <div>
    <v-autocomplete
      clearable
      outlined
      dense
      item-value="id"
      hide-details
      prepend-inner-icon="mdi-calendar-clock-outline"
      :label="$t('filters.completedAt')"
      :items="completedDateOptions"
      :value="selectedFilters.completedAtOption"
      @change="handleDateChange($event)"
    />
    <v-menu
      v-if="showCustomCompletedDate"
      offset-y
      class="mt-4"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on }">
        <v-text-field
          :value="dateRangeText"
          v-on="on"
          dense
          readonly
          hide-details
          outlined
          event-color="primary"
          prepend-inner-icon="mdi-calendar-outline"
          class="font-size-14 my-4"
          :label="$t('common.date_options.date_range')"
        />
      </template>

      <v-date-picker
        v-model="customDateValues"
        style="width: 100%;"
        no-title
        range
        reactive
        :value="dateValues"
        :allowed-dates="date => date <= new Date().toISOString().slice(0, 10)"
        @change="onDateRangeChange($event)"
      />
    </v-menu>
  </div>
</template>

<script>
import * as moment from 'moment'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import filtersConstants from './Filters/constants'
import { getDateRangeText } from './dateRangeText'

const { dates } = utils
const DATE_FILTER_OPTIONS = filtersConstants.DATE_FILTER_OPTIONS
const COMPLETED_DATE_OPTIONS = [
  {
    id: DATE_FILTER_OPTIONS.all,
    textKey: 'common.date_periods.all',
  },
  ...filtersConstants.COMMON_DATE_OPTIONS,
]

export default {
  name: 'CompletedDateSelector',

  props: {
    selectedFilters: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      completedDateOptions: COMPLETED_DATE_OPTIONS.map(option => ({
        id: option.id,
        text: this.$t(option.textKey ?? option.text),
      })),
      customDateValues: [],
    }
  },

  computed: {
    showCustomCompletedDate() {
      return (
        this.selectedFilters?.completedAtOption === DATE_FILTER_OPTIONS.custom
      )
    },

    dateRangeText() {
      return getDateRangeText(Object.values(this.selectedFilters?.completedAt ?? {}))
    },

    dateValues() {
      return _.isEmpty(this.selectedFilters?.completedAt)
        ? []
        : Object.values(this.selectedFilters?.completedAt)
    },
  },

  methods: {
    handleDateChange(dateRange) {
      switch (dateRange) {
        case DATE_FILTER_OPTIONS.all:
          this.formatDateForFilter(DATE_FILTER_OPTIONS.all, [], 'completedAt')
          break
        case DATE_FILTER_OPTIONS.last7Days:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last7Days,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.last7Days)
            ),
            'completedAt'
          )
          break
        case DATE_FILTER_OPTIONS.lastMonth:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.lastMonth,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.lastMonth)
            ),
            'completedAt'
          )
          break
        case DATE_FILTER_OPTIONS.last90Days:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.last90Days,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.last90Days)
            ),
            'completedAt'
          )
          break
        case DATE_FILTER_OPTIONS.thisYear:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.thisYear,
            Object.values(
              dates.getPredefinedDateRange(DATE_FILTER_OPTIONS.thisYear)
            ),
            'completedAt'
          )
          break
        case DATE_FILTER_OPTIONS.custom:
          this.formatDateForFilter(
            DATE_FILTER_OPTIONS.custom,
            [],
            'completedAtOption'
          )
          break
        default:
          this.formatDateForFilter('', [], 'completedAtOption')
      }
    },

    onDateRangeChange(range = []) {
      const [startDate, endDate] = range
      const startDateTime = moment(startDate).format()
      const endDateTime = moment(endDate)
        .endOf('day')
        .format()

      if (startDateTime > endDateTime) {
        this.$emit(
          'change',
          { from: endDateTime, to: startDateTime },
          'completedAt'
        )
        return
      }

      this.$emit(
        'change',
        { from: startDateTime, to: endDateTime },
        'completedAt'
      )
    },

    formatDateForFilter(option, value, key) {
      if (_.isEmpty(value)) {
        this.$emit('change', [], 'completedAt')
        this.$emit('change', option, 'completedAtOption')
        return
      }

      this.$emit(
        'change',
        {
          from: moment(value[0]).format(),
          to: moment(value[1])
            .endOf('day')
            .format(),
        },
        key
      )
      this.$emit('change', option, 'completedAtOption')
    },
  },
}
</script>
